/**
 * Treatment plans & estimates (dental + eye program, phase A2).
 *
 * Staff: /api/treatment-plans — doctors build, present and progress a plan; the front desk and clinic admins read
 * it, print the estimate, record the patient's answer on the clinic's device and raise the plan bill (the advance and
 * instalments are then ordinary partial payments on that bill, POST /api/bills/{billId}/payment).
 * Patients: /api/patient/treatment-plans — never drafts; a family member's record when the account holder has
 * switched to one (fetchPatientApi sends X-Active-Dependent-Patient-Id).
 *
 * Shapes mirror medopd-backend treatmentplan/TreatmentPlanDtos.java exactly.
 */
import { fetchApi, fetchPatientApi, API_BASE_URL, generateRequestId } from '../client';
import type { ApiResponse } from '../client';
import { buildQS } from '../helpers';

const BASE = '/api/treatment-plans';
const PATIENT_BASE = '/api/patient/treatment-plans';

export type PlanStatus = 'DRAFT' | 'PRESENTED' | 'ACCEPTED' | 'IN_PROGRESS' | 'COMPLETED' | 'DECLINED' | 'CANCELLED';
export type ItemStatus = 'PLANNED' | 'ACCEPTED' | 'DECLINED' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
export type SiteKind = 'TOOTH' | 'EYE' | 'AREA' | 'NONE';
export type EyeSide = 'RE' | 'LE' | 'BOTH';
export type Surface = 'M' | 'O' | 'D' | 'B' | 'L' | 'I';
export type SignerRelation = 'SELF' | 'PARENT' | 'GUARDIAN' | 'SPOUSE' | 'CHILD' | 'SIBLING' | 'OTHER';
export type PdfLang = 'en' | 'te' | 'hi';

export interface Money { gross: number; discount: number; net: number; gst: number; total: number }

export interface TreatmentSite {
  kind: SiteKind;
  teeth: number[];
  surfaces: string[];
  eye: EyeSide | null;
  area: string | null;
  label: string | null;
}

export interface Sitting {
  id: string;
  number: number;
  appointmentId: string | null;
  /** Clinical: sent to doctors only. */
  note: string | null;
  doneByName: string | null;
  doneAt: string;
}

export interface PlanItem {
  id: string;
  phaseId: string | null;
  position: number;
  catalogueId: string | null;
  procedureName: string;
  department: string | null;
  site: TreatmentSite;
  quantity: number;
  unitFee: number;
  discountAmount: number;
  gross: number;
  net: number;
  sittingsPlanned: number;
  sittingsDone: number;
  status: ItemStatus;
  optionGroup: string | null;
  optionLabel: string | null;
  recommended: boolean;
  notes: string | null;
  clinicalProcedureId: string | null;
  billId: string | null;
  billed: boolean;
  completedAt: string | null;
  cancelReason: string | null;
  sittings: Sitting[];
}

export interface PlanPhase { id: string; position: number; title: string; notes: string | null }

export interface PlanTotals {
  estimate: Money;
  optionsMin: Money | null;
  optionsMax: Money | null;
  accepted: Money;
  done: Money;
  unbilled: Money;
}

/**
 * `refundDue`: paid beyond what the bill now comes to (treatment on it was cancelled), owed back to the patient.
 * `onlineRefundable`: how much of that can go back online, to what the patient paid with.
 */
export interface PlanBill {
  billId: string; billNumber: string; billDate: string; total: number; paid: number; due: number; status: string;
  refundDue: number; onlineRefundable: number;
}
export interface PlanPayments { billed: number; paid: number; due: number; bills: PlanBill[]; refundDue: number }

export type RefundMode = 'cash' | 'upi' | 'card' | 'cheque' | 'online';
export interface RefundBody { amount: number; mode: RefundMode; reference?: string; reason: string }
export interface RefundResult {
  billId: string; billNumber: string; refunded: number; mode: RefundMode; stillOwed: number; gatewayRefundIds: string[];
}
export interface PlanProgress { itemsTaken: number; itemsDone: number; sittingsPlanned: number; sittingsDone: number }

export interface PlanAnswer {
  id: string;
  decision: 'ACCEPTED' | 'DECLINED';
  signedByName: string | null;
  relation: string | null;
  via: 'IN_CLINIC' | 'PATIENT_APP';
  acceptedTotal: number;
  signed: boolean;
  reason: string | null;
  at: string;
}

export interface TreatmentPlan {
  id: string;
  planNumber: string;
  patientId: string;
  patientName: string | null;
  title: string;
  specialty: string | null;
  status: PlanStatus;
  acceptance: 'FULL' | 'PARTIAL' | null;
  awaitingPatient: boolean;
  doctorUserId: string | null;
  doctorName: string | null;
  appointmentId: string | null;
  notes: string | null;
  terms: string | null;
  validUntil: string | null;
  expired: boolean;
  gstPercent: number;
  presentedAt: string | null;
  acceptedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  phases: PlanPhase[];
  items: PlanItem[];
  totals: PlanTotals;
  payments: PlanPayments;
  progress: PlanProgress;
  answers: PlanAnswer[];
  /** The signed-in doctor may change this plan (server-decided). */
  canEdit: boolean;
}

export interface PlanSummary {
  id: string;
  planNumber: string;
  patientId: string;
  patientName: string | null;
  title: string;
  specialty: string | null;
  status: PlanStatus;
  acceptance: 'FULL' | 'PARTIAL' | null;
  awaitingPatient: boolean;
  doctorName: string | null;
  validUntil: string | null;
  expired: boolean;
  createdAt: string;
  updatedAt: string;
  estimateTotal: number;
  acceptedTotal: number;
  paid: number;
  due: number;
  itemsTaken: number;
  itemsDone: number;
}

export interface PlanPage { content: PlanSummary[]; totalElements: number; page: number; size: number }

export interface PlanEvent { action: string; detail: string | null; itemId: string | null; actorName: string | null; at: string }
export interface BillResult { billId: string; billNumber: string; total: number; items: number }

export interface CreatePlanBody {
  patientId: string;
  title?: string;
  specialty?: string;
  appointmentId?: string;
  notes?: string;
  terms?: string;
  validUntil?: string;
}

export interface UpdatePlanBody { title?: string; specialty?: string; notes?: string; terms?: string; validUntil?: string }
export interface PhaseBody { title?: string; notes?: string; position?: number }

export interface SiteBody { kind: SiteKind; teeth?: number[]; surfaces?: string[]; eye?: EyeSide; area?: string }

export interface ItemBody {
  phaseId?: string | null;
  position?: number;
  catalogueId?: string;
  procedureName?: string;
  department?: string;
  site?: SiteBody;
  quantity?: number;
  unitFee?: number;
  discountAmount?: number;
  sittingsPlanned?: number;
  optionGroup?: string;
  optionLabel?: string;
  recommended?: boolean;
  notes?: string;
}

export interface SittingBody {
  appointmentId?: string;
  note?: string;
  finish?: boolean;
  consentStatus?: 'OBTAINED' | 'NOT_REQUIRED';
}

export interface SignatureStrokes { width: number; height: number; strokes: { x: number; y: number }[][] }

export interface AcceptBody {
  acceptedItemIds: string[];
  signedByName: string;
  relation: SignerRelation;
  /** RAW pen strokes in the pad's own pixel coordinates; the server draws the image. */
  signature: SignatureStrokes;
}

export interface ListFilters { patientId?: string; status?: PlanStatus[]; page?: number; size?: number }

const json = (method: string, body?: unknown): RequestInit =>
  body === undefined ? { method } : { method, body: JSON.stringify(body) };

const id = (v: string) => encodeURIComponent(v);

// ── PDF download ───────────────────────────────────────────────────────────────────────────────────────────────
// The generic fetchGetFileApi / fetchPatientGetFileApi helpers send neither the active-clinic header (a branch user
// would get another clinic's "not found") nor the family-member header (a parent viewing a child's plan would be
// refused). These two send exactly what fetchApi / fetchPatientApi send.

async function blobFrom(response: Response): Promise<ApiResponse<{ blob: Blob; fileName?: string }>> {
  if (!response.ok) {
    const isJson = response.headers.get('content-type')?.includes('application/json');
    const err = isJson ? await response.json().catch(() => ({})) : {};
    return { error: (err as { message?: string }).message || 'We couldn’t prepare the estimate. Please try again.', status: response.status };
  }
  const blob = await response.blob();
  const match = (response.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/);
  return { data: { blob, fileName: match?.[1] } };
}

async function staffPdf(planId: string, lang: PdfLang): Promise<ApiResponse<{ blob: Blob; fileName?: string }>> {
  const headers: Record<string, string> = { 'X-Request-ID': generateRequestId(), 'X-Client-Version': '1.0.0' };
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (sessionToken) headers['X-Session-Token'] = sessionToken;
  const clinic = sessionStorage.getItem('active_clinic_id');
  if (clinic) headers['X-Active-Clinic-Id'] = clinic;
  try {
    return await blobFrom(await fetch(`${API_BASE_URL}${BASE}/${id(planId)}/pdf${buildQS({ lang })}`,
      { method: 'GET', headers, credentials: 'include' }));
  } catch {
    return { error: 'Network error. Please check your connection.' };
  }
}

async function patientPdf(planId: string, lang: PdfLang): Promise<ApiResponse<{ blob: Blob; fileName?: string }>> {
  const headers: Record<string, string> = { 'X-Request-ID': generateRequestId(), 'X-Auth-Transport': 'cookie' };
  const clinic = sessionStorage.getItem('active_patient_clinic_id');
  if (clinic) headers['X-Active-Patient-Clinic-Id'] = clinic;
  const dependent = sessionStorage.getItem('active_dependent_patient_id');
  if (dependent) headers['X-Active-Dependent-Patient-Id'] = dependent;
  try {
    return await blobFrom(await fetch(`${API_BASE_URL}${PATIENT_BASE}/${id(planId)}/pdf${buildQS({ lang })}`,
      { method: 'GET', headers, credentials: 'include' }));
  } catch {
    return { error: 'Network error. Please check your connection.' };
  }
}

// ── Staff ──────────────────────────────────────────────────────────────────────────────────────────────────────

export const treatmentPlansModule = {
  list: (f: ListFilters = {}) =>
    fetchApi<PlanPage>(`${BASE}${buildQS({
      patientId: f.patientId,
      status: f.status && f.status.length ? f.status.join(',') : undefined,
      page: f.page,
      size: f.size,
    })}`),
  get: (planId: string) => fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}`),
  events: (planId: string) => fetchApi<PlanEvent[]>(`${BASE}/${id(planId)}/events`),
  pdf: staffPdf,

  create: (body: CreatePlanBody) => fetchApi<TreatmentPlan>(BASE, json('POST', body)),
  update: (planId: string, body: UpdatePlanBody) => fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}`, json('PUT', body)),

  addPhase: (planId: string, body: PhaseBody) => fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/phases`, json('POST', body)),
  updatePhase: (planId: string, phaseId: string, body: PhaseBody) =>
    fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/phases/${id(phaseId)}`, json('PUT', body)),
  deletePhase: (planId: string, phaseId: string) =>
    fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/phases/${id(phaseId)}`, json('DELETE')),
  reorderPhases: (planId: string, ids: string[]) =>
    fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/phases/reorder`, json('POST', { ids })),

  addItem: (planId: string, body: ItemBody) => fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/items`, json('POST', body)),
  updateItem: (planId: string, itemId: string, body: ItemBody) =>
    fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/items/${id(itemId)}`, json('PUT', body)),
  deleteItem: (planId: string, itemId: string) =>
    fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/items/${id(itemId)}`, json('DELETE')),
  reorderItems: (planId: string, ids: string[]) =>
    fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/items/reorder`, json('POST', { ids })),

  present: (planId: string) => fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/present`, json('POST')),
  reopen: (planId: string) => fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/reopen`, json('POST')),
  cancel: (planId: string, reason: string) => fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/cancel`, json('POST', { reason })),

  recordSitting: (planId: string, itemId: string, body: SittingBody) =>
    fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/items/${id(itemId)}/sittings`, json('POST', body)),
  completeItem: (planId: string, itemId: string, body: SittingBody) =>
    fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/items/${id(itemId)}/complete`, json('POST', body)),
  cancelItem: (planId: string, itemId: string, reason?: string) =>
    fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/items/${id(itemId)}/cancel`, json('POST', { reason })),

  /** The patient signs on the clinic's device; the signed-in staff member is recorded as the witness. */
  accept: (planId: string, body: AcceptBody) => fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/accept`, json('POST', body)),
  decline: (planId: string, reason?: string) => fetchApi<TreatmentPlan>(`${BASE}/${id(planId)}/decline`, json('POST', { reason })),

  /** Raises the plan bill for agreed, unbilled items (all of them when itemIds is left out). */
  bill: (planId: string, itemIds?: string[]) =>
    fetchApi<BillResult>(`${BASE}/${id(planId)}/bill`, json('POST', itemIds ? { itemIds } : {})),

  /** Gives back money owed on a plan bill after treatment on it was cancelled (PAYMENT_REFUND). */
  refund: (planId: string, billId: string, body: RefundBody) =>
    fetchApi<RefundResult>(`${BASE}/${id(planId)}/bills/${id(billId)}/refund`, json('POST', body)),
};

// ── Patient portal ─────────────────────────────────────────────────────────────────────────────────────────────

export const patientTreatmentPlansModule = {
  list: () => fetchPatientApi<PlanSummary[]>(PATIENT_BASE),
  get: (planId: string) => fetchPatientApi<TreatmentPlan>(`${PATIENT_BASE}/${id(planId)}`),
  pdf: patientPdf,
  accept: (planId: string, body: AcceptBody) =>
    fetchPatientApi<TreatmentPlan>(`${PATIENT_BASE}/${id(planId)}/accept`, json('POST', body)),
  decline: (planId: string, reason?: string) =>
    fetchPatientApi<TreatmentPlan>(`${PATIENT_BASE}/${id(planId)}/decline`, json('POST', { reason })),
};
